import type { Artist, HeroImage } from '../lib/types.ts';

/**
 * The arithmetic behind "Program {år}" at the top of the front page (Program 2027), kept apart
 * from the component so it can be tested without rendering.
 *
 * The front-page image is made in the top's own format (1707 × 720: the original with a box on
 * each side), so the top is exactly as tall as the image. The vertical name fills that height,
 * sized in container units: a capital is about 0.64 em wide.
 */

/** The front-page format: width / height. */
export const FRONT_PAGE_ASPECT = 1707 / 720;

/** The phone crop of the same image: the vertical name takes the left 16.4 %. */
export const MOBILE_ASPECT = 1225 / 720;

/** Same tolerance as the BackOffice's format warning (ArtistHeroImages.IsFrontPageFormat). */
export function isFrontPageFormat(image: Pick<HeroImage, 'width' | 'height'>): boolean {
  if (image.width <= 0 || image.height <= 0) return false;
  return Math.abs(image.width / image.height / FRONT_PAGE_ASPECT - 1) <= 0.05;
}

const CAP_WIDTH_EM = 0.64;

/** Font size of the vertical name on desktop, in cqw: the name runs the image's full height, but
 *  never wider than its 11.5 % column. */
export function desktopNameSize(text: string): number {
  return capped((100 / FRONT_PAGE_ASPECT) / (text.length * CAP_WIDTH_EM), 11);
}

/** Font size of the vertical name on a phone, in cqw, in its 16.4 % column. */
export function mobileNameSize(text: string): number {
  return capped((100 / MOBILE_ASPECT) / (text.length * CAP_WIDTH_EM), 20);
}

function capped(size: number, max: number): number {
  return Math.round(Math.min(size, max) * 100) / 100;
}

/** The festival year, from program.json's start date ("2027-09-03" → "2027"). */
export function festivalYear(startDate: string): string {
  return startDate.slice(0, 4);
}

/** The words of the name ticker: the announced names and "Midtfyns {år}" — or, before anyone is
 *  announced, "Første navn til {år}" and a question mark. */
export function tickerWords(artists: readonly Pick<Artist, 'name'>[], year: string): readonly string[] {
  return artists.length > 0
    ? [...artists.map(a => a.name), `Midtfyns ${year}`]
    : [`Første navn til ${year}`, '?'];
}

/** At least `min` words, by repeating the list, so one copy is wider than the screen. The track
 *  then holds two copies and slides by half its width. */
export function tickerLoop(words: readonly string[], min = 12): readonly string[] {
  const loop: string[] = [];
  while (words.length > 0 && loop.length < min) loop.push(...words);
  return loop;
}

/** Seconds for one copy of the loop to pass, at a fixed 40 px/s whatever the names: each word is
 *  about 0.62 em per character, plus the star and the gaps either side. */
export function tickerSeconds(loop: readonly string[], fontPx: number, starPx: number, gapPx: number): number {
  const width = loop.reduce((sum, w) => sum + w.length * 0.62 * fontPx + starPx + 2 * gapPx, 0);
  return Math.round(width / 40 * 10) / 10;
}
